import type { SpecValue, Specs } from "@convex/schemas/validators/specs.lib";
import { isUnknownRecord } from "@shared/validation/narrow";
import type {
  DimensionSpec,
  FileValue as FormFileValue,
  SpecFieldType,
  SpecValue as FormSpecValue,
  SpecsRecord,
} from "@/components/specs/types";

const SPEC_FIELD_TYPES: readonly SpecFieldType[] = [
  "select",
  "number",
  "string",
  "boolean",
  "dimensions",
  "capacity",
  "file",
];

export function toSpecFieldType(type: string): SpecFieldType {
  return SPEC_FIELD_TYPES.find((known) => known === type) ?? "string";
}

type SpecPrimitive = string | number | boolean | null;

function isSpecPrimitive(value: unknown): value is SpecPrimitive {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

export function isFileValue(value: unknown): value is FormFileValue {
  return (
    isUnknownRecord(value) &&
    typeof value.fileId === "string" &&
    typeof value.filename === "string" &&
    (value.url === undefined || typeof value.url === "string")
  );
}

export function isSpecValue(value: unknown): value is SpecValue {
  if (isSpecPrimitive(value)) return true;
  if (Array.isArray(value)) {
    return (
      value.every((entry) => typeof entry === "string") ||
      value.every(isFileValue)
    );
  }
  if (!isUnknownRecord(value)) return false;
  return isFileValue(value) || Object.values(value).every(isSpecPrimitive);
}

export function isSpecs(value: unknown): value is Specs {
  return (
    !Array.isArray(value) &&
    isUnknownRecord(value) &&
    Object.values(value).every(isSpecValue)
  );
}

export function toSpecs(value: unknown): Specs | undefined {
  return isSpecs(value) ? value : undefined;
}

function isFormDimensionEntry(value: unknown): boolean {
  return (
    value === undefined ||
    typeof value === "number" ||
    typeof value === "string"
  );
}

export function isFormDimensionSpec(value: unknown): value is DimensionSpec {
  return (
    isUnknownRecord(value) &&
    !Array.isArray(value) &&
    !isFileValue(value) &&
    Object.values(value).every(isFormDimensionEntry)
  );
}

export function isFormSpecValue(value: unknown): value is FormSpecValue {
  if (value === undefined || isSpecPrimitive(value)) return true;
  if (Array.isArray(value)) {
    return (
      value.every((entry) => typeof entry === "string") ||
      value.every(isFileValue)
    );
  }
  return isFileValue(value) || isFormDimensionSpec(value);
}

export function toSpecsRecord(record: Record<string, unknown>): SpecsRecord {
  const specs: SpecsRecord = {};
  for (const [key, value] of Object.entries(record)) {
    if (isFormSpecValue(value)) specs[key] = value;
  }
  return specs;
}
